import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Minus, Plus, Trash2, MessageCircle, ShoppingBag, Loader2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { buildWhatsappUrl, WHATSAPP_DISPLAY } from "../constants/site";
import { useSiteSettings } from "../hooks/useSiteSettings";
import { CART } from "../constants/testIds";
import { toast } from "sonner";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BASE_URL}/api`;

const formatCartMessage = (items, contact) => {
    const lines = [
        "*Nouvelle commande — Dentiprime*",
        "",
        contact.name ? `• Nom / Cabinet / Clinique : ${contact.name}` : null,
        contact.phone ? `• Téléphone : ${contact.phone}` : null,
        contact.city ? `• Adresse : ${contact.city}` : null,
        "",
        "*Produits demandés :*",
        ...items.map(
            (i, idx) => `${idx + 1}. ${i.name} — quantité : ${i.quantity}`,
        ),
        "",
        `Total articles : ${items.reduce((s, i) => s + i.quantity, 0)}`,
    ].filter(Boolean);
    return lines.join("\n");
};

const CartDrawer = () => {
    const { items, totalQty, open, setOpen, updateQty, removeItem, clear } = useCart();
    const { data: settings } = useSiteSettings();
    const whatsappNumber = settings?.whatsapp_number || WHATSAPP_DISPLAY.replace(/\D/g, "");
    const whatsappDisplay = settings?.whatsapp_display || WHATSAPP_DISPLAY;
    const [contact, setContact] = useState({
        name: "",
        phone: "",
        city: "",
    });
    const [sending, setSending] = useState(false);

    const update = (k) => (e) => setContact((c) => ({ ...c, [k]: e.target.value }));

    const handleCheckout = () => {
        if (items.length === 0) {
            toast.error("Votre panier est vide.");
            return;
        }
        if (!contact.name || !contact.phone) {
            toast.error("Renseignez votre nom et un numéro de téléphone.");
            return;
        }
        setSending(true);

        const url = buildWhatsappUrl(formatCartMessage(items, contact), whatsappNumber);
        const win = window.open(url, "_blank");
        if (!win) {
            window.location.href = url;
        }

        axios
            .post(`${API}/quote-requests`, {
                full_name: contact.name,
                clinic_name: null,
                phone: contact.phone,
                city: contact.city || null,
                message: null,
                items: items.map((i) => ({
                    product_id: i.id,
                    name: i.name,
                    category: i.category || null,
                    quantity: i.quantity,
                })),
            })
            .then(() => {
                toast.success("Commande envoyée sur WhatsApp. Notre équipe vous répond.");
            })
            .catch((err) => {
                console.warn("[cart] backend POST failed", err);
                toast.warning(
                    "WhatsApp ouvert, mais l'enregistrement interne a échoué. Renvoyez le message depuis l'app si besoin.",
                );
            })
            .finally(() => {
                setSending(false);
                setOpen(false);
            });
    };

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetContent
                data-testid={CART.drawer}
                className="w-full sm:max-w-[480px] flex flex-col p-0 bg-white"
            >
                <SheetHeader className="px-6 pt-6 pb-4 border-b border-black/[0.06]">
                    <div className="dp-eyebrow">Panier · WhatsApp</div>
                    <SheetTitle className="font-display text-2xl tracking-tight mt-1 flex items-center justify-between">
                        <span>Votre commande</span>
                        <span className="text-[13px] font-mono-tech text-neutral-500 font-normal">
                            {totalQty} {totalQty > 1 ? "articles" : "article"}
                        </span>
                    </SheetTitle>
                    <SheetDescription className="text-[13px] text-neutral-500">
                        Récapitulatif envoyé sur WhatsApp ({whatsappDisplay}). Aucun paiement
                        en ligne — devis et facturation gérés par notre équipe.
                    </SheetDescription>
                </SheetHeader>

                {/* Items list */}
                <div
                    data-testid={CART.itemsList}
                    className="flex-1 overflow-y-auto px-6 py-4"
                >
                    {items.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-center py-16 text-neutral-400">
                            <ShoppingBag className="w-10 h-10 mb-3" strokeWidth={1.2} />
                            <p className="text-[14px]">Votre panier est vide.</p>
                            <p className="text-[12px] mt-1">
                                Ajoutez des produits depuis le catalogue.
                            </p>
                        </div>
                    ) : (
                        <ul className="space-y-3">
                            {items.map((it) => (
                                <li
                                    key={it.id}
                                    data-testid={CART.item(it.id)}
                                    className="flex items-start gap-3 rounded-2xl border border-black/[0.06] p-3.5"
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="font-display text-[14.5px] font-medium text-neutral-900 truncate">
                                            {it.name}
                                        </div>
                                        {it.category && (
                                            <div className="mt-0.5 text-[11px] font-mono-tech tracking-[0.15em] uppercase text-neutral-400">
                                                {it.category}
                                            </div>
                                        )}
                                        <div className="mt-2 flex items-center gap-2">
                                            <div className="flex items-center gap-1 border border-black/10 rounded-full overflow-hidden">
                                                <button
                                                    type="button"
                                                    onClick={() => updateQty(it.id, it.quantity - 1)}
                                                    data-testid={CART.minus(it.id)}
                                                    className="w-7 h-7 flex items-center justify-center hover:bg-black/[0.04]"
                                                    aria-label="Diminuer"
                                                >
                                                    <Minus className="w-3 h-3" />
                                                </button>
                                                <span
                                                    data-testid={CART.qty(it.id)}
                                                    className="min-w-[18px] text-center text-[12.5px] tabular-nums"
                                                >
                                                    {it.quantity}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => updateQty(it.id, it.quantity + 1)}
                                                    data-testid={CART.plus(it.id)}
                                                    className="w-7 h-7 flex items-center justify-center hover:bg-black/[0.04]"
                                                    aria-label="Augmenter"
                                                >
                                                    <Plus className="w-3 h-3" />
                                                </button>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => removeItem(it.id)}
                                                data-testid={CART.remove(it.id)}
                                                className="text-neutral-400 hover:text-red-500 transition p-1"
                                                aria-label="Supprimer du panier"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}

                    {/* Contact details */}
                    {items.length > 0 && (
                        <div className="mt-6 pt-5 border-t border-black/[0.06]">
                            <div className="dp-eyebrow mb-3">Vos coordonnées</div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="col-span-2 space-y-1.5">
                                    <Label className="text-[11px] font-medium">Nom / Cabinet / Clinique *</Label>
                                    <Input
                                        data-testid={CART.inputName}
                                        value={contact.name}
                                        onChange={update("name")}
                                        placeholder="Dr. Youssef Benali / Cabinet Dentaire Benali"
                                        className="h-10"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-[11px] font-medium">Téléphone *</Label>
                                    <Input
                                        data-testid={CART.inputPhone}
                                        value={contact.phone}
                                        onChange={update("phone")}
                                        placeholder="+212 6 12 34 56 78"
                                        className="h-10"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-[11px] font-medium">Adresse</Label>
                                    <Input
                                        data-testid={CART.inputCity}
                                        value={contact.city}
                                        onChange={update("city")}
                                        placeholder="Hay Msala, Rabat"
                                        className="h-10"
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-black/[0.06] bg-[#FBFBFD] space-y-2.5">
                    <button
                        type="button"
                        onClick={handleCheckout}
                        disabled={items.length === 0 || sending}
                        data-testid={CART.checkout}
                        className="w-full dp-btn dp-btn-primary justify-center text-[14px] py-3 disabled:opacity-50 disabled:cursor-not-allowed bg-[#25D366] hover:bg-[#1ebe57] text-white"
                    >
                        {sending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <MessageCircle className="w-4 h-4" />
                        )}
                        Envoyer la commande sur WhatsApp
                    </button>
                    {items.length > 0 && (
                        <Button
                            variant="ghost"
                            onClick={clear}
                            data-testid={CART.clear}
                            className="w-full rounded-full text-[12.5px] text-neutral-500 hover:text-red-500"
                        >
                            Vider le panier
                        </Button>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
};

export default CartDrawer;
